import './Menu.css';

import { FaFacebook } from '@react-icons/all-files/fa/FaFacebook';
import { FaInstagram } from '@react-icons/all-files/fa/FaInstagram';
import { graphql, useStaticQuery } from 'gatsby';
import React, { Fragment, memo, useEffect, useMemo, useState } from 'react';

import { useScroll } from '../../hooks/useScroll';
import { IGatsbyLocalImage } from '../../types/gatsby';
import { getLocalizedString } from '../../utils/getLocalizedLink';
import compact from '../../utils/helpers/compact';
import { serializeImageForImageComponent } from '../../utils/serializeImageForImageComponent';
import { tw } from '../../utils/tailwind';
import BurgerMenu from '../BurgerMenu/BurgerMenu';
import Cart from '../Cart/Cart';
import { Box, Flex, Stack } from '../Core';
import { addMenuItemsFromContentItems } from '../Footer/utils';
import Image from '../Image';
import LanguageSelector from '../LanguageSelector';
import MenuItem from '../MenuItem';
import StyledLink from '../StyledLink';
import TopMenu from '../TopMenu';

const SCROLL_DISTANCE = -150;

interface IHeaderProps {
  currentLanguage: Language;
  currentPath: string;
  isInstitute?: boolean;
  menuData: MenusData;
  submenuItems: IPageContext['submenuItems'];
  translations: Queries.PageDAccueilQuery['wpPage']['translations'];
  showLanguageSelector: boolean;
  languageSelectorInfoMsg?: string;
  showSnipcart?: boolean;
}

const Header: FCWithClassName<IHeaderProps> = (props) => {
  if (!props.menuData) return null;
  const menuItems = props.isInstitute ? props.menuData.headerinstitute : props.menuData.header;

  const topMenuItems = props.isInstitute ? props.menuData.topmenuinstitute : props.menuData.topmenu;
  const [showHeader, setShowHeader] = useState(true);
  const [headerState, setHeaderState] = useState<'initial' | 'scrolled'>('initial');
  const data = useStaticQuery<{
    logo: IGatsbyLocalImage;
    site: { siteMetadata?: { title?: string; siteUrl?: string } };
  }>(graphql`
    query SiteLogo {
      logo: file(relativePath: { eq: "logo.svg" }) {
        publicURL
        extension
      }
      site {
        siteMetadata {
          title
          siteUrl
        }
      }
    }
  `);
  const { logo, site } = data;
  const siteUrl = site.siteMetadata.siteUrl;
  const serializedLogo = serializeImageForImageComponent({
    alt: `Logo ${site.siteMetadata.title}`,
    image: logo,
  });
  const { currentDistance, direction: scrollDirection, posY } = useScroll();
  useEffect(() => {
    setHeaderState(posY > SCROLL_DISTANCE ? 'initial' : 'scrolled');
    setShowHeader((prevShowHeaderState: boolean) => {
      if (posY > SCROLL_DISTANCE) return true;
      // if the navbar is already visible, we don't want to hide it just because the user doesn't scroll fast enough
      if (scrollDirection === 'up') {
        return prevShowHeaderState || currentDistance >= 30;
      }
      return false;
    });
  }, [posY]);
  const serializedSocialLinks = compact(
    props.menuData.topmenu.map((menuItem) => {
      if (menuItem.label === 'FB') {
        return (
          <a
            key={menuItem.id}
            href={menuItem.url ?? undefined}
            target="_blank"
            rel="noopener noreferrer"
            className="border-b-0 text-text"
          >
            <FaFacebook className="w-[23px] h-[23px] text-text" />
          </a>
        );
      }
      if (menuItem.label === 'IG') {
        return (
          <a
            key={menuItem.id}
            href={menuItem.url ?? undefined}
            target="_blank"
            rel="noopener noreferrer"
            className="border-b-0 text-text"
          >
            <FaInstagram className="w-[23px] h-[23px] text-text" />
          </a>
        );
      }
      return null;
    })
  );
  const menuItemsWithServicesAndCentres = menuItems.map((menuItem) => {
    if (!menuItem) return undefined;
    let menuGroup = menuItem;
    if (menuItem.title === 'centres') {
      menuGroup = addMenuItemsFromContentItems({
        contentItems: props.submenuItems.centres,
        menuGroup: menuItem,
      });
    }
    if (menuItem.title === 'pathologies') {
      menuGroup = addMenuItemsFromContentItems({
        contentItems: props.submenuItems.zonesCorps,
        menuGroup: menuItem,
      });
    }
    return (
      <MenuItem
        allowSubmenu
        currentPath={props.currentPath}
        key={menuGroup.id}
        menuItem={menuGroup}
        menuType="desktop"
      />
    );
  });

  const additionalBurgerMenuItems = compact(
    props.menuData.topmenu.map((menuItem) => {
      if (menuItem.label === 'My U-Perform') {
        return <MenuItem currentPath={props.currentPath} key={menuItem.id} menuItem={menuItem} menuType="burger" />;
      }
      return null;
    })
  );

  const localizedHomepageLink = getLocalizedString('/', '/en', props.currentLanguage.slug, 'fr');

  const memoizedMenuContent = useMemo(
    () => (
      <Fragment>
        <StyledLink to={localizedHomepageLink} variant="navigationLink">
          <Image image={serializedLogo} className="w-40" />
        </StyledLink>
        <Flex justify="flex-end" className="flex-1 ml-0 md:ml-8">
          {props.showSnipcart && <Cart className="flex pb-1 cursor-pointer lg:hidden snipcart-checkout text-text" />}
          <Stack className="hidden xl:flex xl:space-x-10 xxl:space-x-16" isInline align="center">
            {menuItemsWithServicesAndCentres}
            <Box className="mb-[5px]">
              {props?.showLanguageSelector ? (
                <LanguageSelector
                  currentLanguage={props.currentLanguage}
                  pageNotInOtherLanguagesMsg={props?.languageSelectorInfoMsg}
                  translations={props.translations}
                />
              ) : null}
            </Box>
          </Stack>
          <BurgerMenu
            currentPath={props.currentPath}
            className="block ml-3 xl:hidden"
            menuItems={[...menuItemsWithServicesAndCentres, ...additionalBurgerMenuItems]}
            siteUrl={siteUrl}
            {...(props.showLanguageSelector
              ? {
                  languageSelector: (
                    <LanguageSelector
                      currentLanguage={props.currentLanguage}
                      pageNotInOtherLanguagesMsg={props.languageSelectorInfoMsg}
                      translations={props.translations}
                    />
                  ),
                }
              : {})}
            footerElements={serializedSocialLinks}
          />
        </Flex>
      </Fragment>
    ),
    []
  );
  return (
    <Flex
      direction="column"
      className={tw(
        'w-full menu-wrapper menu-transitions fixed z-50 text-text bg-white shadow-lg',
        headerState === 'scrolled' ? 'shadow-lg' : 'shadow-none',
        showHeader ? 'top-0' : 'top-[calc(-90px-40px)]'
      )}
    >
      <TopMenu currentPath={props.currentPath} menuData={topMenuItems} showSnipcart={props.showSnipcart} />
      <Flex align="center" className="w-full px-4 py-2 mx-auto sm:px-8 color-text h-header-height max-w-screen-xxl">
        {memoizedMenuContent}
      </Flex>
    </Flex>
  );
};

export default memo(Header);
